// studioCardMenu — the Studio's card menu and canvas menu as menu rows (pen
// "Studio – detail design" §4). Which entries show is studioMenus.ts; this
// gives each its copy and maps it to one of the canvas's intents. With the
// card selected, an action takes the whole selection (in reading order).

export { useStudioMenus };

import { exhaustSwitchCase } from "@bilda/utils";
import { useLingui } from "@lingui/react/macro";
import type { ContainerKind } from "../../app/containerFacts.js";
import { Icon } from "../Icon/Icon.js";
import type { MenuItem } from "../Menu/Menu.js";
import { readingOrder } from "./studioBox.js";
import type { StudioCard } from "./studioCards.js";
import type { Placement, Point } from "./studioLayout.js";
import {
    type CanvasMenuEntry,
    type CardMenuEntry,
    canvasMenuEntries,
    cardMenuEntries,
    type OffCanvasCard,
} from "./studioMenus.js";

type StudioMenuIntents = {
    readonly onOpen: (card: StudioCard) => void;
    readonly onExport: (card: StudioCard) => void;
    // The cards (any order) into a new container of `kind`.
    readonly putIn: (kind: ContainerKind, ids: readonly string[]) => void;
    readonly onAddToContainer: (
        containerId: string,
        ids: readonly string[],
    ) => void;
    readonly onRemoveFromCanvas: (ids: readonly string[]) => void;
    // Asks first (the confirmation dialog).
    readonly onDelete: (card: StudioCard) => void;
    // The canvas menu's: made at `at` (canvas units).
    readonly onNewModuleAt: (at: Point) => void;
    readonly onUpload: () => void;
    readonly onPutBack: (id: string, at: Point) => void;
};

type StudioMenusInput = {
    readonly cards: readonly StudioCard[];
    readonly selectedIds: readonly string[];
    readonly placements: ReadonlyMap<string, Placement>;
    readonly offCanvas: readonly OffCanvasCard[];
    readonly online: boolean;
    readonly intents: StudioMenuIntents;
};

const useStudioMenus = ({
    cards,
    selectedIds,
    placements,
    offCanvas,
    online,
    intents,
}: StudioMenusInput) => {
    const { t } = useLingui();

    const cardItem =
        (card: StudioCard, ids: readonly string[]) =>
        (entry: CardMenuEntry): readonly MenuItem[] => {
            switch (entry.type) {
                case "open":
                    return [
                        {
                            _tag: "item",
                            label: t`Open`,
                            icon: <Icon name="expand" />,
                            onSelect: () => intents.onOpen(card),
                        },
                    ];
                case "add_to":
                    return [
                        {
                            _tag: "submenu",
                            label:
                                entry.kind === "module"
                                    ? t`Add to module …`
                                    : t`Add to course …`,
                            icon: <Icon name={entry.kind} />,
                            disabled: !online,
                            items: [
                                ...(entry.offerNew
                                    ? [
                                          {
                                              _tag: "item",
                                              label:
                                                  entry.kind === "module"
                                                      ? t`New module`
                                                      : t`New course`,
                                              icon: <Icon name="plus" />,
                                              onSelect: () =>
                                                  intents.putIn(
                                                      entry.kind,
                                                      ids,
                                                  ),
                                          } satisfies MenuItem,
                                      ]
                                    : []),
                                ...entry.targets.map(
                                    (c): MenuItem => ({
                                        _tag: "item",
                                        label: c.title,
                                        onSelect: () =>
                                            intents.onAddToContainer(
                                                c.id,
                                                readingOrder(ids, placements),
                                            ),
                                    }),
                                ),
                            ],
                        },
                    ];
                case "export":
                    return [
                        {
                            _tag: "item",
                            label: t`Export to your learning environment`,
                            icon: <Icon name="export" />,
                            onSelect: () => intents.onExport(card),
                        },
                    ];
                case "remove_from_canvas":
                    return [
                        {
                            _tag: "item",
                            label: t`Remove from canvas`,
                            icon: <Icon name="eye-off" />,
                            onSelect: () => intents.onRemoveFromCanvas(ids),
                        },
                    ];
                case "delete":
                    return [
                        { _tag: "separator" },
                        {
                            _tag: "item",
                            label: t`Delete material …`,
                            icon: <Icon name="trash" />,
                            tone: "danger",
                            disabled: !online,
                            onSelect: () => intents.onDelete(card),
                        },
                    ];
                default:
                    return exhaustSwitchCase(entry);
            }
        };

    const canvasItem =
        (at: Point) =>
        (entry: CanvasMenuEntry): readonly MenuItem[] => {
            switch (entry.type) {
                case "new_module_here":
                    return [
                        {
                            _tag: "item",
                            label: t`New module here`,
                            icon: <Icon name="module" />,
                            disabled: !online,
                            onSelect: () => intents.onNewModuleAt(at),
                        },
                    ];
                case "add_source_here":
                    return [
                        {
                            _tag: "submenu",
                            label: t`Add source here …`,
                            icon: <Icon name="file-up" />,
                            items: [
                                {
                                    _tag: "item",
                                    label: t`Upload files …`,
                                    icon: <Icon name="upload" />,
                                    disabled: !online,
                                    onSelect: intents.onUpload,
                                },
                                ...(entry.offCanvas.length === 0
                                    ? []
                                    : [
                                          {
                                              _tag: "label",
                                              label: t`From the Library`,
                                          } satisfies MenuItem,
                                          ...entry.offCanvas.map(
                                              (c): MenuItem => ({
                                                  _tag: "item",
                                                  label: c.title,
                                                  onSelect: () =>
                                                      intents.onPutBack(
                                                          c.id,
                                                          at,
                                                      ),
                                              }),
                                          ),
                                      ]),
                            ],
                        },
                    ];
                default:
                    return exhaustSwitchCase(entry);
            }
        };

    return {
        // A card's menu; selected, it acts on the whole selection.
        card: (card: StudioCard, isSelected: boolean): readonly MenuItem[] =>
            cardMenuEntries(card, cards).flatMap(
                cardItem(card, isSelected ? selectedIds : [card.id]),
            ),
        // The canvas's menu, for things made at `at` (canvas units).
        canvas: (at: Point): readonly MenuItem[] =>
            canvasMenuEntries(offCanvas).flatMap(canvasItem(at)),
    };
};
